import { useAppDispatch } from '@/app/redux/hooks';
import {
  openConfirmationDialogue,
  setConfirmationDialogueCallback,
} from '@/app/redux/slices/modalsSlice';
import { Group } from '@/common/types/group';
import { OrganisationMember } from '@/common/types/organisation';
import { Student } from '@/common/types/student';
import InitialsCircle from '@/common/ui/InitialsCircle';
import { MagnifyingGlassIcon, PlusIcon, XIcon } from '@phosphor-icons/react';
import { Checkbox } from '@shadcn/components/checkbox';
import { Command, CommandItem, CommandList } from '@shadcn/components/command';
import { Input } from '@shadcn/components/input';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { cn } from '@shadcn/lib/utils';
import { Info } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

interface GenericMultiSelectProps<T> {
  id?: string;
  items: T[];
  itemType: string;
  selectedIds: string[];
  onSelect: (id: string) => void;
  onSelectAll?: () => void;
  onFilter: (searchTerm: string) => T[];
  onAddItem?: () => void;
  autoFocus?: boolean;
  placeholder?: string;
  className?: string;
  getItemId: (item: T) => string;
  getItemName: (item: T) => string;
  getItemSubtitle?: (item: T) => string;
  getItemDisabled?: (item: T) => boolean;
  getDisabledReason?: (item: T) => string;
  disabled?: boolean;
  getConfirmRemoveDescription?: (item: T) => string | string[] | undefined;
}

function GenericMultiSelect<T>({
  id = 'multi-select',
  items,
  itemType,
  selectedIds,
  onSelect,
  onSelectAll,
  onFilter,
  onAddItem,
  autoFocus,
  placeholder = 'Search...',
  className,
  getItemId,
  getItemName,
  getItemSubtitle,
  getItemDisabled,
  getDisabledReason,
  disabled,
  getConfirmRemoveDescription,
}: GenericMultiSelectProps<T>) {
  const dispatch = useAppDispatch();

  const inputRef = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredItems, setFilteredItems] = useState(items);
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);

  useEffect(() => {
    if (autoFocus) {
      // Add a small delay to ensure the input is fully mounted
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  useEffect(() => {
    if (!items) return;
    if (!searchTerm) {
      setFilteredItems(items);
      return;
    }
    const filtered = onFilter(searchTerm);
    setFilteredItems(filtered);
  }, [searchTerm, items, onFilter]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as HTMLElement;
      const input = document.querySelector(`#${id}-input`);
      const commandList = document.querySelector(`#${id}-command-list`);
      if (!input?.contains(target) && !commandList?.contains(target)) {
        setIsOptionsOpen(false);
        setSearchTerm('');
      }
    }
    if (isOptionsOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () =>
        document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [id, isOptionsOpen]);

  function getItemById(itemId: string): T | undefined {
    return items.find((item) => getItemId(item) === itemId);
  }

  function handleSelect(item: T) {
    const itemId = getItemId(item);
    if (getConfirmRemoveDescription && selectedIds.includes(itemId)) {
      handleConfirmRemove(item);
    } else {
      onSelect(itemId);
    }
  }

  function handleConfirmRemove(item: T) {
    const confirmDescription = getConfirmRemoveDescription?.(item);
    if (confirmDescription === undefined) return onSelect(getItemId(item));
    setConfirmationDialogueCallback(async () => {
      onSelect(getItemId(item));
    });
    dispatch(
      openConfirmationDialogue({
        title: `Remove ${getItemName(item)}?`,
        description: confirmDescription,
        variant: 'destructive',
      }),
    );
  }

  function handleAddItem() {
    onAddItem?.();
    setIsOptionsOpen(false);
    setSearchTerm('');
  }

  return (
    <MultiSelectContainer className={className}>
      <SelectedContainer>
        {selectedIds.map((itemId) => {
          const item = getItemById(itemId);
          const name = item ? getItemName(item) : 'Unknown';
          return (
            <Chip key={itemId}>
              <InitialsCircle userName={name} size={20} />
              <span>{name}</span>
              <XIcon
                size={20}
                onClick={(e) => {
                  e.stopPropagation();
                  if (disabled || !item) return;
                  handleSelect(item);
                }}
                className={cn(
                  'cursor-pointer rounded-full p-1',
                  disabled
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:bg-accent',
                )}
              />
            </Chip>
          );
        })}
        {selectedIds.length === 0 && (
          <div className="text-sm text-muted-foreground w-full text-center py-1">
            No {itemType}s selected
          </div>
        )}
      </SelectedContainer>
      <InputContainer>
        <MagnifyingGlassIcon
          size={18}
          className="absolute top-1/2 left-2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          id={`${id}-input`}
          ref={inputRef}
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setSearchTerm(e.target.value);
            setIsOptionsOpen(true);
          }}
          onClick={() => {
            setIsOptionsOpen(true);
          }}
          className="pl-8 border-b-0 border-l-0 border-r-0"
          autoComplete="off"
          disabled={disabled}
        />
      </InputContainer>
      {isOptionsOpen && (
        <OptionsContainer id={`${id}-command-list`}>
          <StyledCommand>
            <StyledCommandList className="font-medium">
              {onSelectAll && (
                <StyledCommandItem onSelect={onSelectAll} asChild>
                  <div className="flex items-center gap-2 w-full">
                    <Checkbox
                      checked={
                        (filteredItems.length > 0 &&
                          selectedIds.length === filteredItems.length) ||
                        'indeterminate'
                      }
                      disabled={filteredItems.length === 0}
                    />
                    Select all
                  </div>
                </StyledCommandItem>
              )}
              {filteredItems.map((item) => {
                const itemId = getItemId(item);
                const name = getItemName(item);
                const subtitle = getItemSubtitle ? getItemSubtitle(item) : '';
                const isDisabled = getItemDisabled ? getItemDisabled(item) : false;
                const disabledReason = isDisabled ? getDisabledReason?.(item) : undefined;
                return (
                  <div className="relative" key={itemId}>
                    <StyledCommandItem
                      value={itemId}
                      onSelect={() => handleSelect(item)}
                      disabled={isDisabled}
                      className={disabledReason ? 'pr-8' : undefined}
                    >
                      <Checkbox checked={selectedIds.includes(itemId)} />
                      <InitialsCircle userName={name} size={20} />
                      <span>{name}</span>
                      {subtitle && (
                        <span className="text-xs text-muted-foreground">
                          {subtitle}
                        </span>
                      )}
                    </StyledCommandItem>
                    {disabledReason && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-primary cursor-default">
                            <Info size={16} strokeWidth={2} />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" align="end" sideOffset={-8} className="z-[1002]">
                          {disabledReason}
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                );
              })}
              {onAddItem && (
                <StyledCommandItem onSelect={handleAddItem}>
                  <PlusIcon size={18} />
                  New {itemType}
                </StyledCommandItem>
              )}
              {!onAddItem && !filteredItems.length && (
                <div className="text-sm text-muted-foreground w-full text-center py-1">
                  No {itemType}s found.
                </div>
              )}
            </StyledCommandList>
          </StyledCommand>
        </OptionsContainer>
      )}
    </MultiSelectContainer>
  );
}

export const LearnerMultiSelect = ({
  id = 'learner-multi-select',
  placeholder = 'Search learners...',
  ...props
}: Omit<
  GenericMultiSelectProps<Student>,
  'itemType' | 'getItemId' | 'getItemName'
>) =>
  GenericMultiSelect<Student>({
    id,
    placeholder,
    ...props,
    itemType: 'learner',
    getItemId: (s) => s.id,
    getItemName: (s) => `${s.studentFirstName} ${s.studentSurname}`,
    getItemSubtitle: (s) => s.spellcastUsername,
  });

export const UserMultiSelect = ({
  id = 'user-multi-select',
  placeholder = 'Search users...',
  ...props
}: Omit<GenericMultiSelectProps<OrganisationMember>, 'itemType'>) =>
  GenericMultiSelect<OrganisationMember>({
    id,
    placeholder,
    ...props,
    itemType: 'user',
  });

export const GroupMultiSelect = ({
  id = 'group-multi-select',
  placeholder = 'Search groups...',
  ...props
}: Omit<
  GenericMultiSelectProps<Group>,
  'itemType' | 'getItemId' | 'getItemName'
>) =>
  GenericMultiSelect<Group>({
    id,
    placeholder,
    ...props,
    itemType: 'group',
    getItemId: (g) => g.id!,
    getItemName: (g) => g.name!,
  });

const MultiSelectContainer = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  max-height: 400px;
  overflow: visible;
`;

const SelectedContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 8px;
  max-height: 200px;
  overflow-y: auto;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: hsl(var(--border));
    border-radius: 3px;
  }
`;

const Chip = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border: 1px solid hsl(var(--border));
  border-radius: 8px;
  font-size: 14px;
  line-height: 0.8;
`;

const InputContainer = styled.div`
  position: relative;
`;

const OptionsContainer = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  width: 100%;
  max-height: 250px;
  border: 1px solid hsl(var(--border));
  border-radius: 6px;
  background-color: hsl(var(--background));
  z-index: 1001;
  box-shadow:
    0 4px 6px -1px rgb(0 0 0 / 0.1),
    0 2px 4px -2px rgb(0 0 0 / 0.1);
  overflow: hidden;
`;

const StyledCommand = styled(Command)`
  max-height: inherit;
`;

const StyledCommandList = styled(CommandList)`
  max-height: 250px;
  overflow-y: auto;
  padding: 4px;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background-color: hsl(var(--border));
    border-radius: 3px;
  }
`;

const StyledCommandItem = styled(CommandItem)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  cursor: pointer;
  border-radius: 4px;
  &:hover {
    background-color: hsl(var(--accent));
  }
  &[data-selected='true'] {
    background-color: hsl(var(--accent));
  }
`;
